import { ACTIVITY_TYPE_LABELS } from '../../constants/activityTypes.js'
/**
 * Comercial → Pipelines — CRM com funil dinâmico (Fase 1 / MVP, DEAL-CENTRIC).
 *
 * Módulo NOVO, independente do Google Ads. NÃO reaproveita pages/leads/*.
 * Consome /api/crm: pipelines → stages (colunas) → deals (CARDS).
 *
 * O card do kanban é o DEAL (a oportunidade). Os Leads (contacts) pertencem ao
 * Deal via deal_contacts (M:N) e não se movem — só o Deal muda de etapa.
 * "Mover card" usa drag-and-drop nativo (sem dependência) e grava a transição
 * no backend (deal_stage_transitions). Soltar numa etapa de ganho/perdido fecha
 * o negócio (status + data de fechamento); ao perder, pede o motivo.
 *
 * Funil/etapa criam via prompt rápido; negócios usam o DealModal (valor,
 * responsável, leads anexados). Editar etapas: PipelineSettings.
 *
 * Layout fiel à referência oficial de design:
 *   integracao-nova/crm/titam-crm-pipelines-refinado-v2.png
 * Estilos em pipelines.css (prefixo `pl-`) — exclusivos desta tela, para não
 * afetar os outros kanbans do app (kbc-* seguem intactos em LeadAnalysis,
 * ProjetoBoard, Operacao, LeadsKanban).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Layout from '../../components/Layout.jsx'
import PipelineChat from './PipelineChat.jsx'
import whatsappIcon from '../../assets/whatsapp.png'
import { cardActivityState } from '../../lib/cardActivity.js'
import Icon from '../../components/icons.jsx'
import { SearchInput } from '../../components/UX.jsx'
import DealModal from './DealModal.jsx'
import DealPanel from './DealPanel.jsx'
import PipelineSettings from './PipelineSettings.jsx'
import PipelineAgenda from './PipelineAgenda.jsx'
import PipelineModal from './PipelineModal.jsx'
import PipelineCreateModal from './PipelineCreateModal.jsx'
import StageCreateModal from './StageCreateModal.jsx'
import DealWinFlow from './DealWinFlow.jsx'
import LossReasonModal from './LossReasonModal.jsx'
import { exportDeals } from './pipelineExport.js'
import { useApp } from '../../context/AppContext.jsx'
import {
  getPipelines,
  updatePipeline,
  seedDefaultPipeline,
  getStages,
  updateStage,
  getDeals,
  getDealsSummary,
  getDealsWhatsappCounters,
  moveDeal,
  fmtBRL,
} from '../../db/api.js'
import '../financeiro/contas.css'
import './clienteView.css'
import './clientes.css'
import './pipelines.css'

const NO_STAGE = '__none__' // coluna para deals sem stage_id
const PAGE_SIZE = 50 // cards carregados por coluna no kanban; o resto vem sob demanda
const STAGE_FETCH_CHUNK = 500 // máximo por requisição aceito pela API em limit_per_stage

// Repõe tudo que o usuário já abriu em cada coluna, em blocos que a API aceita.
async function fetchLoadedDeals(pid, archived, perStage) {
  const first = Math.min(STAGE_FETCH_CHUNK, Math.max(PAGE_SIZE, ...perStage.values()))
  const rows = await getDeals(pid, { archived, limitPerStage: first })
  const extra = await Promise.all([...perStage]
    .filter(([stageId, count]) => stageId && count > first)
    .map(async ([stageId, count]) => {
      const out = []
      for (let offset = first; offset < count; offset += STAGE_FETCH_CHUNK) {
        const page = await getDeals(pid, { archived, limitPerStage: Math.min(STAGE_FETCH_CHUNK, count - offset), stageId, offset })
        out.push(...page)
        if (page.length === 0) break
      }
      return out
    }))
  return rows.concat(...extra)
}
const byUpdatedDesc = (a, b) => (Date.parse(b.updated_at) || 0) - (Date.parse(a.updated_at) || 0) || String(b.id).localeCompare(String(a.id))
const ACTIVITY_TYPES = ACTIVITY_TYPE_LABELS

// R$ sem centavos (o design mostra os valores arredondados: "R$ 12.000")
const brl0 = (v) => `R$ ${Math.round(Number(v) || 0).toLocaleString('pt-BR')}`

const MESES_ABBR = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
const fmtCardDate = (isoStr) => {
  if (!isoStr) return null
  const d = new Date(isoStr)
  if (Number.isNaN(d.getTime())) return null
  return `${d.getDate()} ${MESES_ABBR[d.getMonth()]}.`
}

const PERIODS = [
  { id: 'hoje', label: 'Hoje', comparativo: 'ao dia anterior' },
  { id: 'semana', label: 'Esta semana', comparativo: 'à semana anterior' },
  { id: 'mes', label: 'Este mês', comparativo: 'ao mês anterior' },
  { id: 'ano', label: 'Este ano', comparativo: 'ao ano anterior' },
  { id: 'total', label: 'Todo o período', comparativo: null },
]

// Início do período atual e do período anterior de mesma duração (para o
// comparativo "+N em relação ao mês anterior" da faixa de estatísticas).
// Só usa campos reais (created_at) — sem valores fictícios.
function periodBounds(id) {
  const now = new Date()
  if (id === 'hoje') {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const prevStart = new Date(start); prevStart.setDate(prevStart.getDate() - 1)
    return { start, prevStart, prevEnd: start }
  }
  if (id === 'semana') {
    const dow = (now.getDay() + 6) % 7 // segunda = 0
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dow)
    const prevStart = new Date(start); prevStart.setDate(prevStart.getDate() - 7)
    return { start, prevStart, prevEnd: start }
  }
  if (id === 'ano') {
    const start = new Date(now.getFullYear(), 0, 1)
    const prevStart = new Date(now.getFullYear() - 1, 0, 1)
    return { start, prevStart, prevEnd: start }
  }
  if (id === 'total') return { start: null, prevStart: null, prevEnd: null }
  // mes (padrão)
  const start = new Date(now.getFullYear(), now.getMonth(), 1)
  const prevStart = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  return { start, prevStart, prevEnd: start }
}

// Converte um hex "#rrggbb" em rgba(...) para o tingimento sutil da coluna.
function hexToRgba(hex, alpha) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '')
  if (!m) return `rgba(158,158,158,${alpha})`
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => parseInt(h, 16))
  return `rgba(${r},${g},${b},${alpha})`
}

const fmtListDate = (value) => {
  if (!value) return '—'
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })
}

const EMPTY_FILTERS = {
  owner: '', status: 'padrao', archived: 'exclude', origem: '',
  createdFrom: '', createdTo: '', wonFrom: '', wonTo: '', lostFrom: '', lostTo: '',
  updatedFrom: '', updatedTo: '', minValor: '', maxValor: '',
}

const inDateRange = (value, from, to) => {
  if (!from && !to) return true
  if (!value) return false
  const t = new Date(value).getTime()
  if (Number.isNaN(t)) return false
  if (from && t < new Date(`${from}T00:00:00`).getTime()) return false
  if (to && t > new Date(`${to}T23:59:59.999`).getTime()) return false
  return true
}

// Filtros do kanban/lista. `ignoreStatus` serve às estatísticas (conversão
// precisa enxergar perdidos mesmo quando o kanban os esconde).
function matchesFilters(d, f, { ignoreStatus = false } = {}) {
  if (!ignoreStatus) {
    if (f.status === 'padrao' && d.status === 'perdido') return false
    if (['aberto', 'ganho', 'perdido'].includes(f.status) && d.status !== f.status) return false
  }
  if (f.owner === '__none__') {
    if (d.owner_id) return false
  } else if (f.owner && d.owner_id !== f.owner) return false
  if (f.origem && d.primary_lead_origem !== f.origem) return false
  if (!inDateRange(d.created_at, f.createdFrom, f.createdTo)) return false
  if (!inDateRange(d.updated_at, f.updatedFrom, f.updatedTo)) return false
  if ((f.wonFrom || f.wonTo) && !(d.status === 'ganho' && inDateRange(d.data_fechamento, f.wonFrom, f.wonTo))) return false
  if ((f.lostFrom || f.lostTo) && !(d.status === 'perdido' && inDateRange(d.data_fechamento, f.lostFrom, f.lostTo))) return false
  const valor = Number(d.valor) || 0
  if (f.minValor !== '' && valor < Number(f.minValor)) return false
  if (f.maxValor !== '' && valor > Number(f.maxValor)) return false
  return true
}

function countActiveFilters(f) {
  return Object.keys(EMPTY_FILTERS).filter((k) => f[k] !== EMPTY_FILTERS[k]).length
}

// Os funis de projeto vivem em projetos.pipelines e não aparecem aqui; o filtro é só por segurança.
const onlyCommercial = (list) => list.filter((p) => p.tipo !== 'operacao')

const pipelineStorageKey = (clientId) => `pl:pipeline:${clientId}`

export default function Pipelines() {
  const navigate = useNavigate()
  const location = useLocation()
  const [pipelines, setPipelines] = useState([])
  const [pipelineId, setPipelineId] = useState(null)
  const [stages, setStages] = useState([])
  const [deals, setDeals] = useState([])
  const [summary, setSummary] = useState(null) // todos os negócios do funil, versão enxuta (só no kanban paginado)
  const [loadingStages, setLoadingStages] = useState(() => new Set()) // colunas carregando "mais"
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [dragId, setDragId] = useState(null)         // card sendo arrastado
  const [dropTarget, setDropTarget] = useState(null) // coluna alvo do card
  const [dragStageId, setDragStageId] = useState(null) // etapa sendo arrastada (reorder)
  const [stageOver, setStageOver] = useState(null)     // { id, after } — feedback de inserção
  const [modal, setModal] = useState(null) // DealModal: {dealId} edita | {stageId} cria | null
  const [panelInitialTab, setPanelInitialTab] = useState('historico')
  const [panelDealId, setPanelDealId] = useState(null) // DealPanel: visualização do card
  const [chatDeal, setChatDeal] = useState(null)
  const [companyDetail, setCompanyDetail] = useState(null)
  useEffect(() => { setChatDeal(null) }, [pipelineId])
  const [panelBump, setPanelBump] = useState(0)         // força recarga do painel após editar
  const [configTab, setConfigTab] = useState(null) // Configurações do pipeline: null | pipeline | etapas | permissoes | funis
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [menuOpen, setMenuOpen] = useState(false)          // kebab de ações do pipeline
  const [pipelineModalTarget, setPipelineModalTarget] = useState(null) // pipeline sendo editado
  const [filtrosOpen, setFiltrosOpen] = useState(false)
  const [createPipeOpen, setCreatePipeOpen] = useState(false) // modal criar pipeline
  const [stageModalOpen, setStageModalOpen] = useState(false) // modal criar etapa
  const menuRef = useRef(null)
  const [seeding, setSeeding] = useState(false)
  const [search, setSearch] = useState('')
  const [todayOnly, setTodayOnly] = useState(false)
  const [selectedDeals, setSelectedDeals] = useState(() => new Set())
  const [exportNotice, setExportNotice] = useState('')
  const exportMenuRef = useRef(null)
  useEffect(() => {
    setSelectedDeals(new Set())
    setExportNotice('')
  }, [pipelineId, search, todayOnly])
  const [view, setView] = useState('kanban')                  // kanban | lista
  const [lossPrompt, setLossPrompt] = useState(null) // { dealId, toStageId } — pede motivo ao perder
  const [winPrompt, setWinPrompt] = useState(null)   // { deal } — pergunta se vira cliente / cria projeto ao ganhar
  const [collapsedStages, setCollapsedStages] = useState(() => new Set()) // colunas recolhidas
  const [period, setPeriod] = useState('mes')                // período da faixa de estatísticas
  const [periodOpen, setPeriodOpen] = useState(false)
  const [selectOpen, setSelectOpen] = useState(false)         // dropdown "Funil — X"
  const [pipeDragId, setPipeDragId] = useState(null)  // funil sendo arrastado (reorder no modal)
  const [pipeOver, setPipeOver] = useState(null)       // { id, after }
  const selectRef = useRef(null)
  const periodRef = useRef(null)
  const boardScrollRef = useRef(null)
  const [boardOverflows, setBoardOverflows] = useState(false)

  useEffect(() => {
    const viewport = boardScrollRef.current
    if (!viewport) return
    const updateOverflow = () => {
      setBoardOverflows(viewport.scrollWidth > viewport.clientWidth + 1)
    }
    const observer = new ResizeObserver(updateOverflow)
    observer.observe(viewport)
    if (viewport.firstElementChild) observer.observe(viewport.firstElementChild)
    updateOverflow()
    return () => observer.disconnect()
  }, [loading, pipelineId, view, stages, deals])

  // busca client-side (igual ao Kanban do Google Ads): filtra os deals já
  // carregados por título, lead primário ou responsável.
  const matchesSearch = useCallback((d) => {
    const q = search.trim().toLowerCase()
    if (!q) return true
    if ([d.titulo, d.primary_lead, d.company_name, d.owner_nome, d.primary_lead_email, d.primary_lead_phone, d.company_cnpj]
      .some((v) => (v || '').toLowerCase().includes(q))) return true
    const digits = q.replace(/\D/g, '')
    return digits.length >= 3 && [d.primary_lead_phone, d.company_cnpj]
      .some((v) => (v || '').replace(/\D/g, '').includes(digits))
  }, [search])

  // Mais recentemente atualizado primeiro: ao editar/mover um negócio ele sobe
  // para o topo da coluna (o backend já entrega nessa ordem; reordenar aqui
  // cobre a atualização otimista feita no cliente).
  const filteredDeals = useMemo(() => deals.filter((d) =>
    (!todayOnly || ['today', 'overdue'].includes(cardActivityState(d.next_activity_at))) &&
    matchesSearch(d) && matchesFilters(d, filters)
  ).sort(byUpdatedDesc), [deals, todayOnly, matchesSearch, filters])

  // No kanban paginado `deals` traz só a primeira página de cada coluna; totais,
  // previsão e estatísticas vêm do resumo com todos os negócios do funil.
  const allDeals = summary || deals
  const statsDeals = useMemo(() => allDeals.filter((d) =>
    matchesSearch(d) && matchesFilters(d, filters, { ignoreStatus: true })
  ), [allDeals, matchesSearch, filters])

  const stageTotals = useMemo(() => {
    if (!summary) return null
    const map = new Map()
    for (const d of summary) {
      const key = d.stage_id || NO_STAGE
      const t = map.get(key) || { count: 0, valor: 0 }
      t.count += 1
      if (d.status !== 'perdido') t.valor += Number(d.valor) || 0
      map.set(key, t)
    }
    return map
  }, [summary])

  const ownerOptions = useMemo(() => {
    const map = new Map()
    deals.forEach((d) => { if (d.owner_id) map.set(d.owner_id, d.owner_nome || 'Sem nome') })
    return [...map].map(([id, nome]) => ({ id, nome })).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
  }, [deals])
  const origemOptions = useMemo(
    () => [...new Set(deals.map((d) => d.primary_lead_origem).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [deals],
  )
  const activeFilterCount = countActiveFilters(filters)

  const selectedListCount = filteredDeals.filter((d) => selectedDeals.has(d.id)).length
  const allListSelected = filteredDeals.length > 0 && selectedListCount === filteredDeals.length

  const todayActivityCount = allDeals.filter((d) => cardActivityState(d.next_activity_at) === 'today').length
  const overdueActivityCount = allDeals.filter((d) => cardActivityState(d.next_activity_at) === 'overdue').length

  // Busca, filtros, "hoje", lista e agenda operam sobre todos os negócios; só o
  // kanban "limpo" usa a carga paginada.
  const fullLoad = view !== 'kanban' || !!search.trim() || todayOnly || activeFilterCount > 0 || filtrosOpen
  const fullLoadRef = useRef(fullLoad)
  fullLoadRef.current = fullLoad
  const dealsRef = useRef(deals)
  dealsRef.current = deals

  // Previsão do funil (weighted pipeline): total em aberto + valor esperado
  // (Σ valor × probabilidade da etapa) dos negócios ativos do funil atual.
  const forecast = useMemo(() => {
    const probById = new Map(stages.map((s) => [s.id, s.probabilidade != null ? Number(s.probabilidade) : null]))
    let total = 0, weighted = 0
    for (const d of summary || filteredDeals) {
      if (d.status === 'perdido') continue
      const v = Number(d.valor) || 0
      total += v
      const p = probById.get(d.stage_id)
      if (p != null) weighted += v * p / 100
    }
    return { total, weighted }
  }, [filteredDeals, summary, stages])

  // Faixa de estatísticas: "Negócios" e "Conversão" respeitam o período
  // selecionado (created_at) e trazem o comparativo real com o período
  // anterior de mesma duração. "Em aberto"/"Previsão" são o valor do funil
  // agora (snapshot), por isso não entram no filtro de período.
  const stats = useMemo(() => {
    const { start, prevStart, prevEnd } = periodBounds(period)
    const inRange = (d, from, to) => {
      const t = new Date(d.created_at)
      return t >= from && (!to || t < to)
    }
    const current = start ? statsDeals.filter((d) => inRange(d, start, null)) : statsDeals
    const previous = prevStart ? statsDeals.filter((d) => inRange(d, prevStart, prevEnd)) : []

    const closedRate = (list) => {
      const ganhos = list.filter((d) => d.status === 'ganho').length
      const perdidos = list.filter((d) => d.status === 'perdido').length
      const fechados = ganhos + perdidos
      return fechados > 0 ? (ganhos / fechados) * 100 : null
    }

    const negocios = current.length
    const negociosPrev = previous.length
    const conversao = closedRate(current)
    const conversaoPrev = closedRate(previous)
    const comparativo = PERIODS.find((p) => p.id === period)?.comparativo || null

    return {
      negocios,
      negociosDelta: comparativo ? negocios - negociosPrev : null,
      conversao,
      conversaoDelta: comparativo && conversao != null && conversaoPrev != null ? conversao - conversaoPrev : null,
      comparativo,
    }
  }, [statsDeals, period])

  // Client reativo: `selectedClient.id` do AppContext dispara recarga ao trocar
  // de cliente (o getSelectedClientId() do módulo já foi atualizado pelo context
  // antes deste render). Sem isso, a troca entre 2 clients não recarregava (F5).
  const { selectedClient } = useApp()
  const clientId = selectedClient?.id || null
  const hasClient = !!clientId

  // funil atual + regras de exclusão (espelham as do backend p/ UI coerente).
  const currentPipe = useMemo(
    () => pipelines.find((p) => p.id === pipelineId) || null,
    [pipelines, pipelineId],
  )
  // Regras de exclusão de um funil qualquer (usado no board e no modal
  // "Gerenciar funis", onde cada linha pode ser editada/excluída).
  const deleteInfo = useCallback((p) => {
    if (!p) return { canDelete: false, reason: null }
    if (p.is_default) return { canDelete: false, reason: 'O funil padrão não pode ser excluído.' }
    if (pipelines.length <= 1) return { canDelete: false, reason: 'Não é possível excluir o único funil do cliente.' }
    return { canDelete: true, reason: null }
  }, [pipelines])

  // fecha o kebab / dropdowns ao clicar fora.
  useEffect(() => {
    if (!menuOpen && !selectOpen && !periodOpen) return
    const onDown = (e) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false)
      if (selectOpen && selectRef.current && !selectRef.current.contains(e.target)) setSelectOpen(false)
      if (periodOpen && periodRef.current && !periodRef.current.contains(e.target)) setPeriodOpen(false)
    }
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [menuOpen, selectOpen, periodOpen])

  // Recarrega a lista de funis após excluir e seleciona o primeiro disponível.
  const handlePipelineDeleted = async () => {
    setPipelineModalTarget(null)
    try {
      const ps = await getPipelines()
      setPipelines(onlyCommercial(ps))
      setPipelineId(onlyCommercial(ps)[0]?.id || null)
    } catch (e) {
      setErr(e.message)
    }
  }

  // ── carga inicial + troca de client: recarrega funis do client atual ──────
  // Keyado em `clientId`: ao trocar de cliente, limpa TODO o estado do anterior
  // (funis, etapas, negócios, seleção, busca, modais/painéis abertos) e recarrega
  // — sem F5, sem dados misturados entre clients.
  useEffect(() => {
    // limpa estado do client anterior
    setPipelines([]); setStages([]); setDeals([]); setPipelineId(null)
    setSearch(''); setErr(null)
    setModal(null); setPanelDealId(null); setLossPrompt(null); setWinPrompt(null)
    setConfigTab(null); setPipelineModalTarget(null); setFilters(EMPTY_FILTERS)
    setCreatePipeOpen(false); setStageModalOpen(false); setMenuOpen(false)
    setSelectOpen(false); setFiltrosOpen(false)

    if (!clientId) {
      setLoading(false)
      return
    }
    setLoading(true)
    let alive = true
    ;(async () => {
      try {
        const ps = await getPipelines()
        if (!alive) return
        setPipelines(onlyCommercial(ps))
        let saved = null
        try { saved = localStorage.getItem(pipelineStorageKey(clientId)) } catch { /* storage indisponível */ }
        setPipelineId((onlyCommercial(ps).find((p) => p.id === saved) || onlyCommercial(ps)[0])?.id || null)
      } catch (e) {
        if (alive) setErr(e.message)
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [clientId])

  // Lembra o funil escolhido (por cliente) para sobreviver ao F5.
  useEffect(() => {
    if (!clientId || !pipelineId) return
    try { localStorage.setItem(pipelineStorageKey(clientId), pipelineId) } catch { /* storage indisponível */ }
  }, [clientId, pipelineId])

  // ── carga do board ao trocar de funil ─────────────────────────────────
  const loadBoard = useCallback(async (pid) => {
    if (!pid) {
      setStages([])
      setDeals([])
      setSummary(null)
      return
    }
    setErr(null)
    try {
      const paged = !fullLoadRef.current
      const perStage = new Map()
      for (const d of dealsRef.current) perStage.set(d.stage_id, (perStage.get(d.stage_id) || 0) + 1)
      // Recarregar não descarta as páginas que o usuário já abriu.
      const [st, dl, sm] = await Promise.all([
        getStages(pid),
        paged ? fetchLoadedDeals(pid, filters.archived, perStage) : getDeals(pid, { archived: filters.archived }),
        paged ? getDealsSummary(pid, { archived: filters.archived }) : Promise.resolve(null),
      ])
      setStages(st)
      setDeals(dl)
      setSummary(sm)
    } catch (e) {
      setErr(e.message)
    }
  }, [filters.archived])

  useEffect(() => {
    loadBoard(pipelineId)
  }, [pipelineId, loadBoard, fullLoad])

  // "Carregar mais" de uma coluna: busca a próxima página só dessa etapa.
  const loadMoreStage = async (stageId) => {
    if (loadingStages.has(stageId)) return
    setLoadingStages((prev) => new Set(prev).add(stageId))
    try {
      const offset = dealsRef.current.filter((d) => d.stage_id === stageId).length
      const rows = await getDeals(pipelineId, { archived: filters.archived, limitPerStage: PAGE_SIZE, stageId, offset })
      setDeals((prev) => {
        const seen = new Set(prev.map((d) => d.id))
        return [...prev, ...rows.filter((d) => !seen.has(d.id))]
      })
    } catch (e) {
      setErr(e.message)
    } finally {
      setLoadingStages((prev) => { const next = new Set(prev); next.delete(stageId); return next })
    }
  }

  useEffect(() => {
    if (!pipelineId || view !== 'kanban' || (import.meta.env.DEV && import.meta.env.MODE === 'demo')) return
    let cancelled = false
    let pending = false
    const timer = setInterval(async () => {
      if (pending || document.hidden) return
      pending = true
      try {
        const ids = dealsRef.current.map((d) => d.id)
        const rows = ids.length ? await getDealsWhatsappCounters(ids) : []
        if (!cancelled) {
          const counts = new Map(rows.map((d) => [d.deal_id, { whatsapp_unanswered_count: d.whatsapp_unanswered_count, has_whatsapp: d.has_whatsapp }]))
          setDeals((current) => current.map((d) => counts.has(d.id) ? { ...d, ...counts.get(d.id) } : d))
        }
      } catch { /* conserva a última contagem em falhas temporárias */ }
      finally { pending = false }
    }, 20000)
    return () => { cancelled = true; clearInterval(timer) }
  }, [pipelineId, view, filters.archived])

  // Deep-link "abrir negócio" (ex.: link de volta a partir de um evento da
  // Agenda). DealPanel busca o deal por id sozinho (getDeal), então não
  // depende do funil/coluna estarem carregados — só abrir já basta. Consome o
  // state uma vez e limpa da URL pra "voltar" não reabrir o painel.
  useEffect(() => {
    const id = location.state?.openDealId || new URLSearchParams(location.search).get('deal')
    if (!id) return
    setPanelInitialTab(location.state?.openDealTab === 'atividades' ? 'atividades' : 'historico')
    setPanelDealId(id)
    navigate(location.pathname, { replace: true, state: {} })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.search])

  // ── ações ─────────────────────────────────────────────────────────────
  // Criação de funil via modal (PipelineCreateModal) — sem window.prompt.
  const handleNewPipeline = () => setCreatePipeOpen(true)

  const handlePipelineCreated = (p) => {
    setCreatePipeOpen(false)
    setPipelines((prev) => [...prev, p])
    setPipelineId(p.id) // dispara loadBoard (etapas do template já vêm criadas)
  }

  // Reordena os funis (drag no modal "Gerenciar funis"). Persiste ordem =
  // índice, mesmo padrão de etapas/PipelineSettings.
  const handleReorderPipelines = async (orderedIds) => {
    const byId = new Map(pipelines.map((p) => [p.id, p]))
    const next = orderedIds.map((id) => byId.get(id)).filter(Boolean)
    setPipelines(next) // otimista
    try {
      await Promise.all(
        next.map((p, i) => (p.ordem !== i ? updatePipeline(p.id, { ordem: i }) : null)).filter(Boolean)
      )
    } catch (e) {
      alert(`Erro ao reordenar funis: ${e.message}`)
      try { setPipelines(onlyCommercial(await getPipelines())) } catch { /* mantém otimista */ }
    }
  }

  // Drop na lista de funis (drag nativo, mesmo mecanismo das etapas).
  const handlePipeDrop = (targetId) => {
    const sid = pipeDragId
    const after = pipeOver?.after
    setPipeDragId(null); setPipeOver(null)
    if (!sid) return
    const ids = pipelines.map((p) => p.id)
    const from = ids.indexOf(sid)
    if (from < 0) return
    ids.splice(from, 1)
    let ti = ids.indexOf(targetId)
    if (ti < 0) ti = ids.length
    else if (after) ti += 1
    ids.splice(ti, 0, sid)
    if (ids.every((id, i) => pipelines[i]?.id === id)) return
    handleReorderPipelines(ids)
  }

  const handleSeedDefault = async () => {
    setSeeding(true)
    try {
      const p = await seedDefaultPipeline()
      const ps = await getPipelines()
      setPipelines(onlyCommercial(ps))
      setPipelineId(p.id)
    } catch (e) {
      alert(`Erro ao criar funil padrão: ${e.message}`)
    } finally {
      setSeeding(false)
    }
  }

  // Criação de etapa via modal (StageCreateModal) — sem window.prompt.
  const handleNewStage = () => { if (pipelineId) setStageModalOpen(true) }

  const handleStageCreated = async () => {
    setStageModalOpen(false)
    await loadBoard(pipelineId)
  }

  const handleNewDeal = (stageId) => {
    setModal({ stageId: stageId === NO_STAGE ? null : stageId })
  }

  const toggleStageCollapsed = (stageId) => {
    setCollapsedStages((prev) => {
      const next = new Set(prev)
      next.has(stageId) ? next.delete(stageId) : next.add(stageId)
      return next
    })
  }

  // Confirma o movimento no backend e reflete o deal atualizado (status/data de
  // fechamento vêm carimbados quando a etapa é de ganho/perdido).
  const commitMove = async (id, stageId, extra = {}) => {
    try {
      const updated = await moveDeal(id, { toStageId: stageId, ...extra })
      setDeals((prev) => prev.map((d) => (d.id === id ? { ...d, ...updated } : d)))
      setSummary((prev) => prev && prev.map((d) => (d.id === id ? { ...d, stage_id: updated.stage_id ?? stageId, status: updated.status ?? d.status } : d)))
    } catch (e) {
      alert(`Erro ao mover: ${e.message}`)
      loadBoard(pipelineId)
    }
  }

  const handleDrop = async (stageId) => {
    setDropTarget(null)
    const id = dragId
    setDragId(null)
    if (!id || stageId === NO_STAGE) return
    const card = deals.find((d) => d.id === id)
    if (!card || card.stage_id === stageId) return
    const target = stages.find((s) => s.id === stageId)
    // otimista: move o card de coluna já; status vem depois do backend
    setDeals((prev) => prev.map((d) => (d.id === id ? { ...d, stage_id: stageId } : d)))
    setSummary((prev) => prev && prev.map((d) => (d.id === id ? { ...d, stage_id: stageId } : d)))
    // etapa de perda → pede o motivo antes de confirmar (o move só grava ao OK)
    if (target?.is_perdido) {
      setLossPrompt({ dealId: id, toStageId: stageId })
      return
    }
    await commitMove(id, stageId)
    // etapa de ganho → oferece transformar a venda em projeto (módulo Projetos)
    if (target?.is_ganho) setWinPrompt({ deal: card })
  }

  // ── reorder de ETAPAS via drag nativo (mesmo mecanismo dos cards) ──────
  // Persiste ordem = índice, igual ao PipelineSettings. `targetId` pode ser um
  // stage.id (insere antes/depois conforme `after`) ou '__end__' (fim).
  const handleStageDrop = async (targetId) => {
    const sid = dragStageId
    const after = stageOver?.after
    setDragStageId(null); setStageOver(null)
    if (!sid) return

    const arr = [...stages]
    const from = arr.findIndex((s) => s.id === sid)
    if (from < 0) return
    const [moved] = arr.splice(from, 1)
    let ti
    if (targetId === '__end__') {
      ti = arr.length
    } else {
      ti = arr.findIndex((s) => s.id === targetId)
      if (ti < 0) ti = arr.length
      else if (after) ti += 1
    }
    arr.splice(ti, 0, moved)

    // sem mudança real? aborta
    if (arr.every((s, i) => stages[i]?.id === s.id)) return

    setStages(arr) // otimista
    try {
      await Promise.all(
        arr.map((s, i) => (s.ordem !== i ? updateStage(s.id, { ordem: i }) : null)).filter(Boolean)
      )
      await loadBoard(pipelineId)
    } catch (e) {
      alert(`Erro ao reordenar etapas: ${e.message}`)
      loadBoard(pipelineId) // reverte
    }
  }

  // ── colunas: stages + "Sem etapa" se houver órfãos ────────────────────
  const columns = [...stages]
  const orphans = filteredDeals.filter((d) => !d.stage_id)
  if (orphans.length) columns.unshift({ id: NO_STAGE, nome: 'Sem etapa', _virtual: true })

  // ── render ────────────────────────────────────────────────────────────
  if (!hasClient) {
    return (
      <Layout title="Pipelines" breadcrumb={[{ label: 'Comercial' }, { label: 'Pipelines' }]}>
        <Empty msg="Selecione um cliente para ver o funil comercial." />
      </Layout>
    )
  }

  const activePeriod = PERIODS.find((p) => p.id === period) || PERIODS[2]

  return (
    <Layout title="Pipelines" breadcrumb={[{ label: 'Comercial' }, { label: 'Pipelines' }]}>
      {/* ═══ Barra de ações: seletor de funil, busca, filtros, novo negócio ═══ */}
      <div className="pl-toolbar">
        <div className="pl-toolbar-left">
          <div className="pl-new-deal">
            <button
              type="button"
              className="pl-new-deal-main"
              disabled={!pipelineId}
              onClick={() => setModal({ stageId: stages[0]?.id || null })}
            >
              <Icon n="plus" s={15} sw={2} c="#fff" /> Negócio
            </button>
            <details className="pl-new-deal-options">
              <summary aria-label="Escolher etapa do novo negócio" title="Escolher etapa do novo negócio">
                <svg width="10" height="6" viewBox="0 0 10 6" aria-hidden="true"><path d="M1 1h8L5 5z" fill="currentColor" /></svg>
              </summary>
              <div className="pl-select-menu" role="menu">
                {stages.map((stage) => (
                  <button key={stage.id} type="button" className="pl-select-item" role="menuitem"
                    onClick={(event) => {
                      event.currentTarget.closest('details').open = false
                      setModal({ stageId: stage.id })
                    }}>
                    Criar em {stage.nome}
                  </button>
                ))}
              </div>
            </details>
          </div>

        </div>

        {pipelineId && (
          <div className="pl-search-wrap">
            <SearchInput
              placeholder="Buscar negócio, lead, empresa, e-mail, telefone ou CNPJ"
              value={search}
              onChange={setSearch}
            />
          </div>
        )}

        {pipelineId && (
          <button
            type="button"
            className={`pl-ghost-btn pl-today-btn${todayOnly ? ' active' : ''}`}
            aria-pressed={todayOnly}
            title="Mostrar atividades de hoje e atrasadas"
            aria-label={`Atividades de hoje: ${todayActivityCount}. Atrasadas: ${overdueActivityCount}`}
            onClick={() => {
              setTodayOnly(view === 'kanban' ? !todayOnly : true)
              setView('kanban')
            }}
          >
            <Icon n="calendar" s={15} /> Atividades de hoje
            <span className="pl-activity-counts" aria-hidden="true">
              <span className="pl-today-count" title="Atividades de hoje">{todayActivityCount}</span>
              <span className="pl-today-count pl-overdue-count" title="Atividades atrasadas">{overdueActivityCount}</span>
            </span>
          </button>
        )}

        <div className="pl-toolbar-right">
          <div className="cl-views">
            <button type="button" className={`cl-view${view === 'kanban' ? ' active' : ''}`}
              onClick={() => { setTodayOnly(false); setView('kanban') }}>
              <Icon n="columns" s={15} /> Kanban
            </button>
            <button type="button" className={`cl-view${view === 'agenda' ? ' active' : ''}`}
              onClick={() => { setTodayOnly(false); setView('agenda') }}>
              <Icon n="calendar" s={15} /> Agenda
            </button>
            <button type="button" className={`cl-view${view === 'lista' ? ' active' : ''}`}
              onClick={() => { setTodayOnly(false); setView('lista') }}>
              <Icon n="menu" s={15} /> Lista
            </button>
          </div>
          <div className="pl-select-wrap pl-funnel-controls" ref={selectRef}>
            <button className="pl-select-btn" onClick={() => setSelectOpen((o) => !o)} disabled={!pipelines.length}>
              <span>{currentPipe?.nome || 'Selecione'}</span>
              <Icon n="chevron_d" s={14} c="var(--ter)" />
            </button>
            {selectOpen && (
              <div className="pl-select-menu" role="menu">
                {pipelines.map((p) => (
                  <button
                    key={p.id}
                    className={`pl-select-item ${p.id === pipelineId ? 'active' : ''}`}
                    onClick={() => { setPipelineId(p.id); setSelectOpen(false) }}
                  >
                    <span className="pl-select-dot" />
                    <span className="pl-select-item-label">{p.nome}</span>
                  </button>
                ))}
                <div className="pl-select-sep" />
                <button className="pl-select-item" onClick={() => { setSelectOpen(false); handleNewPipeline() }}>
                  <Icon n="plus" s={13} sw={2} c="var(--blue)" />
                  <span className="pl-select-item-label" style={{ color: 'var(--blue)', fontWeight: 700 }}>
                    Novo pipeline
                  </span>
                </button>
              </div>
            )}
          </div>
          {view !== 'agenda' && (
            <button className="pl-ghost-btn pl-filter-control" onClick={() => setFiltrosOpen(true)}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true"><path d="M4 6h16M7 11h10M10 16h4" /></svg>
              Filtro{activeFilterCount > 0 && <span className="pl-filter-badge">{activeFilterCount}</span>} <Icon n="chevron_d" s={12} />
            </button>
          )}
          <div className="cv-topbar-more-wrap" ref={menuRef}>
            <button
              type="button"
              className="pl-icon-btn"
              title="Configurações do pipeline"
              aria-label="Configurações do pipeline"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <Icon n="more" s={18} />
            </button>
            {menuOpen && (
              <div className="cv-topbar-menu" style={{ width: 300 }} role="menu">
                <button role="menuitem"
                  onClick={() => { setMenuOpen(false); setPipelineModalTarget(null); setConfigTab(pipelineId ? 'pipeline' : 'funis') }}>
                  <Icon n="settings" s={15} /> Configurações do pipeline
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {err && <div className="fc-error">{err}</div>}
      {loading && <div className="cv-muted">Carregando…</div>}

      {!loading && !pipelines.length && (
        <div className="cv-muted" style={{ padding: '48px 16px' }}>
          <div style={{ marginBottom: 12 }}>Nenhum funil ainda.</div>
          <button className="cv-btn cv-btn-primary" onClick={handleSeedDefault} disabled={seeding}>
            {seeding ? 'Criando…' : 'Criar funil padrão'}
          </button>
          <span style={{ margin: '0 8px' }}>ou</span>
          <button className="cv-btn cv-btn-ghost" onClick={handleNewPipeline}>Novo funil vazio</button>
        </div>
      )}

      {/* Kanban — visual próprio (pl-*), independente do design dos outros
          kanbans do app. */}
      {!loading && pipelineId && view === 'kanban' && !columns.length && (
        <div className="pl-no-stages">
          <div className="pl-no-stages-title">Este funil ainda não tem etapas</div>
          <div className="pl-no-stages-sub">Crie as etapas para começar a organizar seus negócios no kanban.</div>
          <button className="cv-btn cv-btn-primary" onClick={() => setConfigTab('etapas')}>
            <Icon n="settings" s={15} /> Configurar etapas
          </button>
        </div>
      )}

      {!loading && pipelineId && view === 'kanban' && columns.length > 0 && (
        <div ref={boardScrollRef} className={`pl-board-scroll${boardOverflows ? ' has-overflow' : ''}`}>
          <div className="pl-board" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(240px, 1fr))` }}>
            {columns.map((stage) => {
              const cards = filteredDeals.filter((d) =>
                stage.id === NO_STAGE ? !d.stage_id : d.stage_id === stage.id
              )
              const accent = stage._virtual ? '#9e9e9e' : stage.cor || '#0071e3'
              const totals = stageTotals?.get(stage.id)
              const ativos = cards.filter((d) => d.status !== 'perdido')
              const valorTotal = totals ? totals.valor : ativos.reduce((s, d) => s + (Number(d.valor) || 0), 0)
              const totalCount = totals ? totals.count : cards.length
              const remaining = Math.max(0, totalCount - cards.length)
              const isTarget = dropTarget === stage.id
              const stageDropStyle = dragStageId && stageOver?.id === stage.id
                ? { boxShadow: stageOver.after ? 'inset -3px 0 0 var(--blue)' : 'inset 3px 0 0 var(--blue)' }
                : null
              const draggingSelf = dragStageId === stage.id ? { opacity: 0.45 } : null
              const collapsed = collapsedStages.has(stage.id)
              // arrastando um card sobre esta coluna (que não é a de origem): mostra
              // o placeholder de destino ("Solte aqui para mover"), igual à referência.
              const showGhost = isTarget && dragId && cards.every((d) => d.id !== dragId)

              return (
                <div
                  key={stage.id}
                  className={`pl-col ${isTarget ? 'drop-active' : ''}`}
                  style={{
                    ...stageDropStyle,
                    ...draggingSelf,
                  }}
                  onDragOver={(e) => {
                    if (dragStageId) {
                      // reorder de etapa: define antes/depois pelo meio da coluna
                      if (stage._virtual) return
                      e.preventDefault()
                      const r = e.currentTarget.getBoundingClientRect()
                      const after = e.clientX > r.left + r.width / 2
                      setStageOver((s) => (s && s.id === stage.id && s.after === after ? s : { id: stage.id, after }))
                    } else {
                      // drag de card (comportamento original)
                      e.preventDefault()
                      if (!stage._virtual) setDropTarget(stage.id)
                    }
                  }}
                  onDragLeave={() => {
                    if (dragStageId) setStageOver((s) => (s && s.id === stage.id ? null : s))
                    else setDropTarget((t) => (t === stage.id ? null : t))
                  }}
                  onDrop={() => (dragStageId ? handleStageDrop(stage.id) : handleDrop(stage.id))}
                >
                  <div
                    className="pl-col-head"
                    style={{ cursor: stage._virtual ? 'default' : 'grab', borderTop: `2px solid ${hexToRgba(accent, 0.55)}` }}
                    draggable={!stage._virtual}
                    onDragStart={stage._virtual ? undefined : (e) => {
                      setDragStageId(stage.id)
                      e.dataTransfer.effectAllowed = 'move'
                      e.dataTransfer.setData('text/plain', `stage:${stage.id}`)
                    }}
                    onDragEnd={() => { setDragStageId(null); setStageOver(null) }}
                  >
                    <div className="pl-col-title-row">
                      <span className="pl-col-name">{stage.nome}</span>
                      <span className="pl-col-count">Negócios {totalCount}</span>
                    </div>
                    <span className="pl-col-value">{brl0(valorTotal)}</span>
                  </div>

                  {!collapsed && (
                    <div className="pl-cards">
                      {cards.map((d) => {
                        const activityState = cardActivityState(d.next_activity_at)
                        const dateLabel = activityState === 'missing' ? null : fmtCardDate(d.next_activity_at)
                        const activityType = ACTIVITY_TYPES[d.next_activity_type] || d.next_activity_type || 'Atividade'
                        const activityText = dateLabel ? `${dateLabel} ${activityType}` : null
                        const activityLabel = { missing: 'Sem próxima atividade', overdue: 'Atividade atrasada', today: 'Atividade hoje', future: 'Próxima atividade' }[activityState]
                        return (
                          <div
                            key={d.id}
                            className={`pl-card pl-card--${d.status === 'perdido' ? 'lost' : activityState} ${dragId === d.id ? 'is-dragging' : ''}`}
                            draggable
                            onDragStart={(e) => {
                              setDragId(d.id)
                              // setData é exigido p/ o drag iniciar no Firefox.
                              e.dataTransfer.setData('text/plain', d.id)
                              e.dataTransfer.effectAllowed = 'move'
                            }}
                            onDragEnd={() => {
                              setDragId(null)
                              setDropTarget(null)
                            }}
                            onClick={() => setPanelDealId(d.id)}
                          >
                            <div className="pl-card-heading">
                              <div className="pl-card-title" title={d.titulo || undefined}>{d.titulo || 'Sem título'}</div>
                              <div className="pl-card-tools">
                              {d.status === 'perdido' ? (
                              <span className="pl-card-status pl-card-status--lost" title="Negócio perdido" aria-label="Negócio perdido">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M17 14V2" /><path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88Z" /></svg>
                              </span>
                              ) : (
                              <span className="pl-card-status" title={activityLabel} aria-label={activityLabel}>
                                <Icon n={activityState === 'overdue' ? 'other' : activityState === 'missing' ? 'alert' : activityState === 'today' ? 'arrow_right' : 'calendar'} s={14} c={activityState === 'missing' ? '#ca8a04' : activityState === 'today' ? 'var(--green)' : activityState === 'overdue' ? 'var(--red)' : 'var(--ter)'} />
                              </span>
                              )}
                              <button type="button" className={`pl-card-whatsapp${d.has_whatsapp === false ? ' is-unavailable' : ''}`} draggable={false} title={d.has_whatsapp === false ? 'Sem WhatsApp associado — abrir negócio' : 'Abrir conversa no WhatsApp'} aria-label={`Abrir WhatsApp de ${d.primary_lead || d.titulo}`} onPointerDown={(e) => e.stopPropagation()} onDragStart={(e) => { e.preventDefault(); e.stopPropagation() }} onClick={(e) => { e.stopPropagation(); if (d.has_whatsapp === false) setPanelDealId(d.id); else setChatDeal(d) }}><img src={whatsappIcon} alt="" width={20} height={20} draggable={false} />{d.has_whatsapp !== false && Number(d.whatsapp_unanswered_count) > 0 && <span className="pl-whatsapp-count" aria-label={`${d.whatsapp_unanswered_count} mensagens sem resposta`}>{d.whatsapp_unanswered_count > 99 ? '99+' : d.whatsapp_unanswered_count}</span>}</button>
                              </div>
                            </div>
                            {d.primary_lead ? <div className="pl-card-contact">{d.primary_lead}</div> : null}
                            {d.company_name ? <div className="pl-card-company">{d.company_name}</div> : null}

                            <div className="pl-card-mid">
                              <span className="pl-card-value">{brl0(d.valor)}</span>
                              {d.status === 'perdido' && <span className="pl-card-lost">Perdido</span>}
                              {d.archived && <span className="pl-card-archived">Arquivado</span>}
                            </div>
                            {dateLabel && <div className="pl-card-foot">
                              <span className={`pl-card-date pl-card-date--${activityState}`} title={`${activityLabel}${d.next_activity_title ? `: ${d.next_activity_title}` : ''}`} aria-label={`${activityLabel}${activityText ? `: ${activityText}` : ''}`}>
                                {dateLabel && <span><span className="pl-card-date-label">{dateLabel}</span> {activityType}</span>}
                              </span>
                            </div>}
                          </div>
                        )
                      })}

                      {showGhost ? (
                        <div className="pl-drop-ghost">Solte aqui<br />para mover</div>
                      ) : cards.length === 0 ? (
                        <div className="pl-empty">Sem negócios</div>
                      ) : null}

                      {remaining > 0 && !stage._virtual && (
                        <button type="button" className="pl-add pl-load-more" disabled={loadingStages.has(stage.id)} onClick={() => loadMoreStage(stage.id)}>
                          {loadingStages.has(stage.id) ? 'Carregando…' : `Carregar mais (${remaining})`}
                        </button>
                      )}

                      {!stage._virtual && (
                        <button className="pl-add" onClick={() => handleNewDeal(stage.id)}>
                          <Icon n="plus" s={14} sw={2} /> Adicionar negócio
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )
            })}

          </div>
        </div>
      )}

      {!loading && pipelineId && view === 'agenda' && (
        <PipelineAgenda key={pipelineId} pipelineId={pipelineId} deals={deals} search={search} dueOnly={todayOnly} onOpenDeal={setPanelDealId} />
      )}

      {/* Lista compacta, com estilos restritos à tela de Pipelines. */}
      {!loading && pipelineId && view === 'lista' && (
        <div className="pl-list-section">
          <div className="pl-list-toolbar">
            <span aria-live="polite">{selectedListCount ? `${selectedListCount} ${selectedListCount === 1 ? 'oportunidade selecionada' : 'oportunidades selecionadas'}` : `${filteredDeals.length} oportunidades`}</span>
            {selectedListCount > 0 && <button type="button" className="pl-list-clear" onClick={() => setSelectedDeals(new Set())}>Limpar seleção</button>}
            <details className="pl-export" ref={exportMenuRef}>
              <summary>Exportar <Icon n="chevron_d" s={12} /></summary>
              <div className="pl-export-menu">
                <span className="pl-export-caption">{selectedListCount ? `Selecionadas (${selectedListCount})` : `Todas da lista (${filteredDeals.length})`}</span>
                {['CSV', 'Excel', 'JSON', 'PDF'].map((format) => (
                  <button type="button" key={format} onClick={() => {
                    const rows = selectedListCount ? filteredDeals.filter((d) => selectedDeals.has(d.id)) : filteredDeals
                    try { setExportNotice(exportDeals(format, rows, stages, currentPipe?.nome)) }
                    catch { setExportNotice(`Não foi possível exportar em ${format}.`) }
                    if (exportMenuRef.current) exportMenuRef.current.open = false
                  }}>Exportar em {format}</button>
                ))}
              </div>
            </details>
          </div>
          {exportNotice && <div className="pl-export-notice" role="status">{exportNotice}</div>}
        <div className="cl-card pl-list">
          {!filteredDeals.length ? (
            <div className="cl-state">Nenhum negócio encontrado.</div>
          ) : (
            <div className="cl-table-scroll">
              <table className="cl-table">
                <thead>
                  <tr>
                    <th className="pl-list-check"><input type="checkbox" aria-label="Selecionar todas as oportunidades da lista" checked={allListSelected}
                      ref={(node) => { if (node) node.indeterminate = selectedListCount > 0 && !allListSelected }}
                      onChange={(e) => setSelectedDeals(e.target.checked ? new Set(filteredDeals.map((d) => d.id)) : new Set())} /></th>
                    <th>Título</th>
                    <th>Valor</th>
                    <th>Empresa</th>
                    <th>Pessoa de contato</th>
                    <th>Origem</th>
                    <th>Próxima atividade em</th>
                    <th>Status no funil</th>
                    <th>Proprietário</th>
                    <th className="cl-col-acoes" aria-label="Ações" />
                  </tr>
                </thead>
                <tbody>
                  {filteredDeals.map((d) => (
                    <tr key={d.id} className={selectedDeals.has(d.id) ? 'is-selected' : ''} onClick={() => setPanelDealId(d.id)}>
                      <td className="pl-list-check" onClick={(e) => e.stopPropagation()}><input type="checkbox" aria-label={`Selecionar ${d.titulo || 'negócio sem título'}`} checked={selectedDeals.has(d.id)} onChange={(e) => {
                        const checked = e.target.checked
                        setSelectedDeals((current) => { const next = new Set(current); if (checked) next.add(d.id); else next.delete(d.id); return next })
                      }} /></td>
                      <td title={d.titulo}><button type="button" className="pl-list-title" onClick={(e) => { e.stopPropagation(); setPanelDealId(d.id) }}>{d.titulo || 'Sem título'}</button></td>
                      <td className="cl-receita">{brl0(d.valor)}</td>
                      <td title={d.company_name}>{d.company_name || '—'}</td>
                      <td title={d.primary_lead}>{d.primary_lead || '—'}</td>
                      <td>{d.primary_lead_origem || '—'}</td>
                      <td>{fmtListDate(d.next_activity_at)}</td>
                      <td>{stages.find((stage) => stage.id === d.stage_id)?.nome || ({ aberto: 'Aberto', ganho: 'Ganho', perdido: 'Perdido' }[d.status]) || '—'}</td>
                      <td title={d.owner_nome}>{d.owner_nome || '—'}</td>
                      <td className="cl-col-acoes">
                        <button type="button" className="cl-icon-btn" title="Editar negócio"
                          onClick={(e) => { e.stopPropagation(); setModal({ dealId: d.id }) }}>
                          <Icon n="edit" s={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </div>
      )}

      {/* Painel de visualização do Deal (abre ao clicar no card). Editar aqui
          reabre o DealModal; criar/editar seguem intactos no DealModal. Não
          renderiza o painel enquanto o modal está aberto — os dois são drawers
          na mesma faixa direita da tela, então o modal deve substituir o
          painel (não ficar atrás dele). */}
      {chatDeal && <PipelineChat key={chatDeal.id} deal={chatDeal} onIdentitySaved={(changes) => { setChatDeal((current) => ({ ...current, ...changes })); setDeals((rows) => rows.map((d) => d.id === chatDeal.id ? { ...d, ...changes } : d)); loadBoard(pipelineId) }} onPhoneSaved={() => loadBoard(pipelineId)} onOpenDeal={() => { setPanelDealId(chatDeal.id); setChatDeal(null) }} onOpenCompany={(contact) => { setCompanyDetail({ deal: chatDeal, contact }); setChatDeal(null) }} onClose={() => setChatDeal(null)} onAnswered={() => { if (import.meta.env.DEV && import.meta.env.MODE === 'demo') setDeals((rows) => rows.map((d) => d.id === chatDeal.id ? { ...d, whatsapp_unanswered_count: 0 } : d)); else loadBoard(pipelineId) }} />}
      {companyDetail && <div className="fc-modal-overlay" onMouseDown={() => setCompanyDetail(null)}>
        <section className="fc-modal pl-company-detail" role="dialog" aria-modal="true" aria-label="Detalhes da empresa" onMouseDown={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === 'Escape') setCompanyDetail(null) }}>
          <header><div><small>Empresa</small><h2>{companyDetail.deal.company_name}</h2></div><button type="button" autoFocus aria-label="Fechar detalhes da empresa" onClick={() => setCompanyDetail(null)}><Icon n="close" s={18} /></button></header>
          <p>Dados vinculados a esta oportunidade</p>
          <dl><dt>Contato</dt><dd>{companyDetail.contact.id ? <button onClick={() => navigate(`/comercial/leads/${encodeURIComponent(companyDetail.contact.id)}`)}>{companyDetail.deal.primary_lead}</button> : companyDetail.deal.primary_lead || '—'}</dd><dt>WhatsApp</dt><dd>{companyDetail.contact.phone || '—'}</dd><dt>Oportunidade</dt><dd><button onClick={() => { setPanelDealId(companyDetail.deal.id); setCompanyDetail(null) }}>{companyDetail.deal.titulo}</button></dd></dl>
        </section>
      </div>}
      {panelDealId && !modal && (
        <DealPanel
          key={`${panelDealId}:${panelBump}`}
          dealId={panelDealId}
          initialTab={panelInitialTab}
          onOpenChat={(selectedDeal) => setChatDeal(selectedDeal)}
          // recarrega o board ao fechar: itens editados no painel mudam deals.valor
          // (sincronizado no backend) → o card do kanban reflete o total atualizado.
          onClose={() => { setPanelDealId(null); setPanelInitialTab('historico'); loadBoard(pipelineId) }}
          onEdit={(id) => setModal({ dealId: id })}
          // "+ Adicionar negócio" na aba Negócios: cria um novo deal no funil atual
          // já anexando o lead principal do deal aberto (quando houver).
          onCreateDeal={(lead) => setModal({
            stageId: stages[0]?.id || null,
            initialContact: lead?.contact_id ? { id: lead.contact_id, nome: lead.nome } : null,
          })}
          onDeleted={() => { setPanelDealId(null); setPanelInitialTab('historico'); loadBoard(pipelineId) }}
          // ação rápida Ganho/Perdido no painel: reflete no board por baixo sem
          // precisar fechar o painel.
          onChanged={() => loadBoard(pipelineId)}
        />
      )}

      {modal && (
        <DealModal
          dealId={modal.dealId || null}
          pipelineId={pipelineId}
          stageId={modal.stageId || null}
          initialContact={modal.initialContact}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null)
            loadBoard(pipelineId)
            // painel aberto: remonta p/ refletir edição OU o novo negócio na lista.
            if (panelDealId) setPanelBump((n) => n + 1)
          }}
          onWon={(deal) => setWinPrompt({ deal })}
        />
      )}

      {/* Motivo da perda — abre ao soltar um card numa etapa "perdido". */}
      {lossPrompt && (
        <LossReasonModal
          onCancel={() => {
            setLossPrompt(null)
            loadBoard(pipelineId) // reverte o move otimista (não confirmado)
          }}
          onConfirm={async ({ lossReasonId, justificativa }) => {
            const { dealId, toStageId } = lossPrompt
            setLossPrompt(null)
            await commitMove(dealId, toStageId, { lossReasonId, justificativa })
          }}
        />
      )}

      {/* Negócio ganho → pergunta se vira cliente e, depois, se cria um projeto. */}
      {winPrompt && (
        <DealWinFlow
          deal={winPrompt.deal}
          onClose={() => setWinPrompt(null)}
          onClientConverted={() => loadBoard(pipelineId)}
          onProjectCreated={(proj) => { setWinPrompt(null); navigate(`/projetos/${proj.id}`) }}
        />
      )}

      {/* Configurações do pipeline — um único popup com abas. */}
      {configTab === 'pipeline' && (pipelineModalTarget || currentPipe) && (
        <PipelineModal
          key={(pipelineModalTarget || currentPipe).id}
          pipeline={pipelineModalTarget || currentPipe}
          tabs={<PipelineConfigTabs active={configTab} onChange={setConfigTab} hasPipeline={!!pipelineId} />}
          canDelete={deleteInfo(pipelineModalTarget || currentPipe).canDelete}
          deleteBlockReason={deleteInfo(pipelineModalTarget || currentPipe).reason}
          onClose={() => { setConfigTab(null); setPipelineModalTarget(null) }}
          onSaved={(updated) => {
            setPipelines((prev) => prev.map((p) => (p.id === updated.id ? updated : p)))
            setPipelineModalTarget(null)
            setConfigTab(null)
          }}
          onDeleted={() => { setConfigTab(null); handlePipelineDeleted() }}
        />
      )}

      {configTab === 'etapas' && pipelineId && (
        <PipelineSettings
          pipelineId={pipelineId}
          pipelineNome={currentPipe?.nome || 'Funil'}
          tabs={<PipelineConfigTabs active={configTab} onChange={setConfigTab} hasPipeline />}
          onClose={() => setConfigTab(null)}
          onSaved={() => {
            setConfigTab(null)
            loadBoard(pipelineId)
          }}
        />
      )}

      {configTab === 'permissoes' && (
        <PipelinePermissionsModal
          tabs={<PipelineConfigTabs active={configTab} onChange={setConfigTab} hasPipeline={!!pipelineId} />}
          onClose={() => setConfigTab(null)}
        />
      )}

      {configTab === 'funis' && (
        <ManageFunilsModal
          pipelines={pipelines}
          activeId={pipelineId}
          tabs={<PipelineConfigTabs active={configTab} onChange={setConfigTab} hasPipeline={!!pipelineId} />}
          onSelect={(id) => { setPipelineId(id); setConfigTab(null) }}
          onNew={() => { setConfigTab(null); handleNewPipeline() }}
          onEdit={(p) => { setPipelineModalTarget(p); setConfigTab('pipeline') }}
          onClose={() => setConfigTab(null)}
          pipeDragId={pipeDragId}
          setPipeDragId={setPipeDragId}
          pipeOver={pipeOver}
          setPipeOver={setPipeOver}
          onDrop={handlePipeDrop}
        />
      )}

      {filtrosOpen && (
        <FiltrosModal
          filters={filters}
          owners={ownerOptions}
          origens={origemOptions}
          onApply={(next) => { setFilters(next); setFiltrosOpen(false) }}
          onClose={() => setFiltrosOpen(false)}
        />
      )}

      {/* Criar pipeline (com template) — substitui o window.prompt. */}
      {createPipeOpen && (
        <PipelineCreateModal
          onClose={() => setCreatePipeOpen(false)}
          onCreated={handlePipelineCreated}
        />
      )}

      {/* Criar etapa — substitui o window.prompt. */}
      {stageModalOpen && pipelineId && (
        <StageCreateModal
          pipelineId={pipelineId}
          ordem={stages.length}
          onClose={() => setStageModalOpen(false)}
          onCreated={handleStageCreated}
        />
      )}

    </Layout>
  )
}

function Empty({ msg }) {
  return (
    <div className="cv-muted">{msg}</div>
  )
}

// ── Modal "Gerenciar funis" ──────────────────────────────────────────────────
// Lista todos os funis do client com reorder por drag (mesmo mecanismo dos
// cards/etapas), seleção do funil ativo e atalhos para criar/editar.
function PipelineConfigTabs({ active, onChange, hasPipeline }) {
  const tabs = [
    { id: 'pipeline', label: 'Editar pipeline', needsPipeline: true },
    { id: 'etapas', label: 'Etapas', needsPipeline: true },
    { id: 'permissoes', label: 'Permissões' },
    { id: 'funis', label: 'Gerenciar funis' },
  ]
  return (
    <div className="pl-config-tabs" role="tablist" aria-label="Configurações do pipeline">
      {tabs.map((t) => (
        <button key={t.id} type="button" role="tab" aria-selected={active === t.id}
          className={`pl-config-tab${active === t.id ? ' active' : ''}`}
          disabled={t.needsPipeline && !hasPipeline}
          onClick={() => onChange(t.id)}>{t.label}</button>
      ))}
    </div>
  )
}

function ManageFunilsModal({
  pipelines, activeId, onSelect, onNew, onEdit, onClose, tabs,
  pipeDragId, setPipeDragId, pipeOver, setPipeOver, onDrop,
}) {
  return (
    <div className="fc-modal-overlay" onMouseDown={onClose}>
      <div className="fc-modal" style={{ width: 720, maxWidth: '96vw' }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="fc-modal-head">
          <span className="fc-drawer-title">Configurações do pipeline</span>
          <button className="fc-drawer-x" onClick={onClose}><Icon n="x" s={16} sw={2} /></button>
        </div>
        {tabs}
        <div className="fc-modal-body">
        <p style={{ margin: '0 0 4px', fontSize: 13, color: '#667085' }}>Selecione, reordene (arraste) ou edite os funis deste cliente.</p>

        <div className="pl-manage-list">
          {pipelines.map((p) => {
            const dropStyle = pipeDragId && pipeOver?.id === p.id
              ? { boxShadow: pipeOver.after ? 'inset 0 -2px 0 var(--blue)' : 'inset 0 2px 0 var(--blue)' }
              : null
            const dragging = pipeDragId === p.id ? { opacity: 0.45 } : null
            return (
              <div
                key={p.id}
                className={`pl-manage-row ${p.id === activeId ? 'active' : ''}`}
                style={{ ...dropStyle, ...dragging }}
                onClick={() => onSelect(p.id)}
                draggable
                onDragStart={(e) => {
                  setPipeDragId(p.id)
                  e.dataTransfer.effectAllowed = 'move'
                  e.dataTransfer.setData('text/plain', `pipeline:${p.id}`)
                }}
                onDragEnd={() => { setPipeDragId(null); setPipeOver(null) }}
                onDragOver={(e) => {
                  if (!pipeDragId) return
                  e.preventDefault()
                  const r = e.currentTarget.getBoundingClientRect()
                  const after = e.clientY > r.top + r.height / 2
                  setPipeOver((o) => (o && o.id === p.id && o.after === after ? o : { id: p.id, after }))
                }}
                onDragLeave={() => setPipeOver((o) => (o && o.id === p.id ? null : o))}
                onDrop={() => onDrop(p.id)}
                title={p.nome}
              >
                <span className="pl-manage-grip"><Icon n="grip" s={14} c="var(--ter)" /></span>
                <span className="pl-manage-name">{p.nome}</span>
                {p.is_default && <span className="pl-manage-default">padrão</span>}
                <button
                  className="pl-manage-edit"
                  onClick={(e) => { e.stopPropagation(); onEdit(p) }}
                  title="Editar funil"
                >
                  <Icon n="edit" s={14} />
                </button>
              </div>
            )
          })}
          {!pipelines.length && <div className="cv-empty">Nenhum funil ainda.</div>}
        </div>
        </div>

        <div className="fc-modal-foot" style={{ justifyContent: 'space-between' }}>
          <button className="fc-ctrl" onClick={onNew}>
            <Icon n="plus" s={14} sw={2} /> Novo pipeline
          </button>
          <button className="fc-ctrl" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  )
}

// ── Modal de motivo da perda ────────────────────────────────────────────────
// Aparece ao arrastar um negócio para uma etapa "perdido". Lista os motivos do
// client (loss_reasons) e permite criar um novo na hora. Justificativa opcional.
// ── Permissões da pipeline (scaffold) ───────────────────────────────────────
// Placeholder consistente com os scaffolds do CRM (FilesTab/ServiceTab): o
// controle de acesso por funil/atendente depende de backend ainda inexistente.
function PipelinePermissionsModal({ onClose, tabs }) {
  return (
    <div className="fc-modal-overlay" onMouseDown={onClose}>
      <div className="fc-modal" style={{ width: 720, maxWidth: '96vw' }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="fc-modal-head">
          <span className="fc-drawer-title">Configurações do pipeline</span>
          <button className="fc-drawer-x" onClick={onClose}><Icon n="x" s={16} sw={2} /></button>
        </div>
        {tabs}
        <div className="fc-modal-body">
          <p style={{ margin: '0 0 4px', fontSize: 13, color: '#667085' }}>
            Adicione acesso para os atendentes e configure as permissões de cada um.
          </p>
          <div className="cv-empty">
            Em breve — controle de acesso por atendente chega com o módulo de permissões.
          </div>
        </div>
        <div className="fc-modal-foot">
          <button className="fc-ctrl" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  )
}

// ── Filtros do kanban/lista ──────────────────────────────────────────────────
function FiltrosModal({ filters, owners, origens, onApply, onClose }) {
  const [draft, setDraft] = useState(filters)
  const set = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }))
  const range = (label, from, to) => (
    <div className="fc-field">
      <label className="fc-label">{label}</label>
      <div className="pl-filter-range">
        <input type="date" className="fc-input" value={draft[from]} onChange={set(from)} aria-label={`${label} — de`} />
        <span>até</span>
        <input type="date" className="fc-input" value={draft[to]} onChange={set(to)} aria-label={`${label} — até`} />
      </div>
    </div>
  )
  return (
    <div className="fc-modal-overlay" onMouseDown={onClose}>
      <div className="fc-modal" style={{ width: 540, maxWidth: '96vw' }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="fc-modal-head">
          <span className="fc-drawer-title">Filtros</span>
          <button className="fc-drawer-x" onClick={onClose}><Icon n="x" s={16} sw={2} /></button>
        </div>
        <div className="fc-modal-body">
          <div className="pl-filter-grid">
            <div className="fc-field">
              <label className="fc-label">Responsável</label>
              <select className="fc-select" value={draft.owner} onChange={set('owner')}>
                <option value="">Todos</option>
                <option value="__none__">Sem responsável</option>
                {owners.map((o) => <option key={o.id} value={o.id}>{o.nome}</option>)}
              </select>
            </div>
            <div className="fc-field">
              <label className="fc-label">Status</label>
              <select className="fc-select" value={draft.status} onChange={set('status')}>
                <option value="padrao">Abertos e ganhos (padrão)</option>
                <option value="todos">Todos, inclusive perdidos</option>
                <option value="aberto">Somente abertos</option>
                <option value="ganho">Somente ganhos</option>
                <option value="perdido">Somente perdidos</option>
              </select>
            </div>
            <div className="fc-field">
              <label className="fc-label">Arquivados</label>
              <select className="fc-select" value={draft.archived} onChange={set('archived')}>
                <option value="exclude">Ocultar arquivados</option>
                <option value="all">Incluir arquivados</option>
                <option value="only">Somente arquivados</option>
              </select>
            </div>
            <div className="fc-field">
              <label className="fc-label">Origem do lead</label>
              <select className="fc-select" value={draft.origem} onChange={set('origem')}>
                <option value="">Todas</option>
                {origens.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="fc-field">
              <label className="fc-label">Valor mínimo (R$)</label>
              <input type="number" min="0" className="fc-input" value={draft.minValor} onChange={set('minValor')} />
            </div>
            <div className="fc-field">
              <label className="fc-label">Valor máximo (R$)</label>
              <input type="number" min="0" className="fc-input" value={draft.maxValor} onChange={set('maxValor')} />
            </div>
          </div>
          {range('Data de criação', 'createdFrom', 'createdTo')}
          {range('Data de ganho', 'wonFrom', 'wonTo')}
          {range('Data de perdido', 'lostFrom', 'lostTo')}
          {range('Última atualização', 'updatedFrom', 'updatedTo')}
        </div>
        <div className="fc-modal-foot" style={{ justifyContent: 'space-between' }}>
          <button className="fc-ctrl" onClick={() => setDraft(EMPTY_FILTERS)}>Limpar filtros</button>
          <span style={{ display: 'flex', gap: 8 }}>
            <button className="fc-ctrl" onClick={onClose}>Cancelar</button>
            <button className="fc-ctrl primary" onClick={() => onApply(draft)}>Aplicar</button>
          </span>
        </div>
      </div>
    </div>
  )
}
